/*
 * Participant
 *
 * A Participant is a subset of a User attached to an Organization or Project
 */
import { mergeRight, pick, tagged } from '@range.io/functional'
import StringTypes from '../string-types.js'

// ---------------------------------------------------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------------------------------------------------

// prettier-ignore
const Participant = tagged('Participant', {
    id            : StringTypes.Id,
    firstName     : 'String',
    lastName      : 'String',
    isSuspended   : 'Boolean?',
    avatarUrl     : StringTypes.OptionalImageUri,
    
    // required for participants; disallowed for removed participant (to be implemented)
    email           : StringTypes.OptionalEmail,
    timezone        : StringTypes.OptionalTimezone,
    organizationRole: StringTypes.OptionalOrganizationRole
})

/*
 * Create a new Participant merging an old one with values from changes
 * @sig update :: (Participant, {k:v}) -> Participant
 */
Participant.update = (participant, changes) => Participant.from(mergeRight(participant, changes))

// @sig fullName :: User -> String
Participant.fullName = p => (p ? `${p.firstName} ${p?.lastName}` : '')

// @sig avatarFallbackText :: User -> Character
Participant.avatarFallbackText = participant => Participant.fullName(participant)?.[0] || ' '

/*
 * Used as a fallback for an avatar
 */
Participant.initialLetter = p => p.firstName?.[0].toUpperCase() || ' '

// @sig nickname :: Participant -> String
Participant.nickname = p => (p ? `${p.firstName} ${p?.lastName[0]}` : '')

// ---------------------------------------------------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Create a Participant from a JSON object
 * @sig fromFirebase :: {k:v} -> Participant
 */
Participant.fromFirebase = o => Participant.from(mergeRight({ isSuspended: false }, o))

const fields = ['id', 'email', 'firstName', 'lastName', 'avatarUrl', 'timezone', 'organizationRole']
Participant.toFirebase = participant => pick(fields, participant)

/*
 * Does the participant's fullName include s?
 */
Participant.matchesFullName = (participant, s) =>
    Participant.fullName(participant).toLowerCase().includes(s.toLowerCase())

// The singleton "unassigned tasks" Participant
let unassignedTaskParticipant
Participant.unassignedTaskParticipant = () => {
    if (!unassignedTaskParticipant)
        unassignedTaskParticipant = Participant.from({
            id: '4db05060-896f-4481-ae74-11190f85700b',
            firstName: 'Unassigned',
            lastName: 'Tasks',
            isSuspended: false,
        })

    return unassignedTaskParticipant
}

/*
 * Extend our prototype with more functions -- but only if we're using Redux. We don't really want this file to know
 * about Redux per se, but it's nice to have these additional functions defined here if we ARE using Redux, so there's
 * only one place to look for them.
 *
 * @see store.js for calls to extendForRedux
 */
// prettier-ignore
Participant.extendForRedux = (getState, defineGetter) => {
    defineGetter(Participant, 'avatarFallbackText', _this => Participant.avatarFallbackText(_this))
    defineGetter(Participant, 'fullName',           _this => Participant.fullName(_this))
}
export default Participant
